import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { trackInternalEvent } from '@rio/tracking'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import * as SimpleForm from 'builder/components/SimpleForm'
import { useI18n } from 'builder/hooks/useI18n'
import { AutoApplyForm } from 'builder/modules/autoApply/types'
import { selectors } from 'builder/modules/autoApply/autoApplyModule'
import { Routes, TotalPageCount } from '../../constants'
import { Container, FillWarning, LayoutContainer, Title, TitleWrapper } from '../styles'
import { ConfirmModal } from '../ConfirmModal/ConfirmModal'
import { usePatchForm } from '../../hooks/usePatchForm'
import { usePrompt } from '../../hooks/usePrompt'
import { useStepsConfig } from '../../hooks/useStepsConfig'
import { FormContainer } from './styles'
import { ContactInfoForm } from './ContactInfoForm'

const TRANSLATION = 'builder.auto_apply.form.contact_information'

const mapForm = (
  form: Pick<
    AutoApplyForm,
    'socialLinksAttributes' | 'location' | 'fullAddress' | 'email' | 'phoneNumber'
  >,
) => {
  const clone = structuredClone(form)
  clone.socialLinksAttributes = clone.socialLinksAttributes.filter(s => !!s.link)
  return clone
}

export const ContactInfoFormContainer = () => {
  const formState = useSelector(selectors.form)
  const form = useForm<
    Pick<
      AutoApplyForm,
      'socialLinksAttributes' | 'location' | 'fullAddress' | 'email' | 'phoneNumber'
    >
  >({
    mode: 'onChange',
  })

  useEffect(() => {
    const { socialLinksAttributes, location, fullAddress, email, phoneNumber } = formState
    form.setValue('socialLinksAttributes', socialLinksAttributes)
    form.setValue('location', location)
    form.setValue('fullAddress', fullAddress)
    form.setValue('email', email)
    form.setValue('phoneNumber', phoneNumber)
  }, [form, formState])

  const { i18n } = useI18n()
  const navigate = useNavigate()
  const stepsConfig = useStepsConfig()
  const { mutateAsync: patchForm, isLoading } = usePatchForm()
  const { isPromptVisible, handleCancel, handleConfirm } = usePrompt({
    when: true,
    ignoreParentRoute: ['auto-apply'],
  })

  const onNextStep = async () => {
    const data = mapForm(form.getValues())

    await patchForm(data)
    navigate(`/auto-apply/${stepsConfig[Routes.CONTACT_INFO].next}`)
  }

  const onBackClick = () => {
    form.clearErrors()
    navigate(`/auto-apply/${stepsConfig[Routes.CONTACT_INFO].prev}`)
  }

  useEffect(() => {
    trackInternalEvent('aa_contact_information', {
      label: 'total_job_search_solution',
    })
  }, [])

  return (
    <LayoutContainer>
      <Container>
        <TitleWrapper>
          <Title>{i18n.t(`${TRANSLATION}.title`)}</Title>
          <FillWarning>{i18n.t(`${TRANSLATION}.sub_title`)}</FillWarning>
        </TitleWrapper>
        <FormContainer>
          <SimpleForm.Form
            submitButtonText={i18n.t(`${TRANSLATION}.next`)}
            onBackClick={onBackClick}
            onSubmit={form.handleSubmit(onNextStep)}
            isPending={isLoading}
            processIndicator={{
              totalStep: TotalPageCount,
              currentStep: stepsConfig[Routes.CONTACT_INFO].order,
            }}
          >
            <ContactInfoForm form={form} />
          </SimpleForm.Form>
        </FormContainer>
      </Container>
      {isPromptVisible && <ConfirmModal onClose={handleCancel} onConfirm={handleConfirm} />}
    </LayoutContainer>
  )
}
